import type { ClubTheme } from './themes';
import type { SocialAnnotation } from './types';

interface AnnotationPopoverProps {
  annotation: SocialAnnotation;
  theme: ClubTheme;
  onStartDiscussion?: (annotationId: string) => void;
}

const TYPE_LABELS: Record<string, { emoji: string; label: string; color: string }> = {
  note:          { emoji: '📝', label: 'Note', color: '#F59E0B' },
  'fact-check':  { emoji: '🛡️', label: 'Fact Check', color: '#10B981' },
  question:      { emoji: '❓', label: 'Question', color: '#3B82F6' },
};

const VERDICT_CONFIG: Record<string, { emoji: string; color: string; bg: string }> = {
  verified:      { emoji: '✅', color: '#10B981', bg: '#10B98120' },
  disputed:      { emoji: '⚠️', color: '#F59E0B', bg: '#F59E0B20' },
  unverifiable:  { emoji: '❓', color: '#6B7280', bg: '#6B728020' },
  misleading:    { emoji: '🚫', color: '#EF4444', bg: '#EF444420' },
};

export default function AnnotationPopover({ annotation, theme, onStartDiscussion }: AnnotationPopoverProps) {
  const typeConfig = TYPE_LABELS[annotation.annotation_type] || TYPE_LABELS.note;
  const verdictConfig = annotation.verdict ? VERDICT_CONFIG[annotation.verdict] : null;
  const authorName = annotation.author?.full_name || 'Unknown';

  return (
    <div
      className="w-72 rounded-xl p-3 shadow-xl"
      style={{
        backgroundColor: theme.colors.surface,
        border: `1px solid ${theme.colors.muted}20`,
      }}
      onMouseEnter={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <span
          className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold"
          style={{ backgroundColor: `${typeConfig.color}20`, color: typeConfig.color }}
        >
          {typeConfig.emoji} {typeConfig.label}
        </span>
        <span className="text-[10px]" style={{ color: theme.colors.muted }}>
          {authorName}
        </span>
      </div>

      {/* Verdict (for fact-checks) */}
      {verdictConfig && (
        <div
          className="mt-2 inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-semibold"
          style={{ backgroundColor: verdictConfig.bg, color: verdictConfig.color }}
        >
          {verdictConfig.emoji} {annotation.verdict}
        </div>
      )}

      {/* Body */}
      {annotation.body && (
        <p className="mt-2 text-xs leading-relaxed" style={{ color: theme.colors.text }}>
          {annotation.body}
        </p>
      )}

      {/* Sources (for fact-checks) */}
      {annotation.sources && annotation.sources.length > 0 && (
        <div className="mt-2 space-y-1">
          <p className="text-[10px] font-semibold" style={{ color: theme.colors.muted }}>Sources:</p>
          {annotation.sources.map((src, i) => (
            <a
              key={i}
              href={src.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block truncate text-[10px] underline hover:opacity-80"
              style={{ color: theme.colors.primary }}
            >
              {src.title || src.url}
            </a>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="mt-2 flex gap-2">
        {annotation.thread_post_id ? (
          <span className="text-[10px]" style={{ color: theme.colors.accent }}>
            💬 Discussion active
          </span>
        ) : onStartDiscussion ? (
          <button
            type="button"
            onClick={() => onStartDiscussion(annotation.id)}
            className="text-[10px] font-semibold hover:opacity-80"
            style={{ color: theme.colors.primary }}
          >
            💬 Start discussion
          </button>
        ) : null}
      </div>
    </div>
  );
}
